'use client';

import { useOrganizations } from '@contexts/OrganizationsContext';
import { useRouter } from '@contexts/Router/RouterContext';
import { JV_CHINA_RESTRICTED_COUNTRIES_SET } from '@lib/constants/restrictedCountries';
import { useFlags } from '@lib/flags/amplitude/client';
import { FEATURE_FLAG_ON, Flags } from '@lib/flags/flagConstants';
import { CompassUIPageRoutes } from '@lib/util/routes';
import { Prohibit } from 'phosphor-icons';
import { Dropdown } from 'ui-components';
import { getWithoutLocale } from 'utils';

type OrganizationSwitcherProps = {
  switchOrganizationMicrocopy?: string;
  restrictedOrganizationMicrocopy?: string;
};

const isRestrictedOrganization = (countries: string[] = []) =>
  countries.some((country) => JV_CHINA_RESTRICTED_COUNTRIES_SET.has(country));

const isOnJvChinaAccessDeniedPath = (pathname: string) =>
  (getWithoutLocale(pathname) || '/') ===
  CompassUIPageRoutes.ACCESS_DENIED_JV_CHINA;

export const OrganizationSwitcher = ({
  switchOrganizationMicrocopy = 'Switch Organization',
  restrictedOrganizationMicrocopy = 'Restricted organization',
}: OrganizationSwitcherProps) => {
  const router = useRouter();
  const { flags } = useFlags();
  const {
    organizations,
    activeOrganization,
    handleOrgChange,
    errorFetchingOrganizations,
  } = useOrganizations();
  const isJvChinaRestrictedAccessEnabled =
    flags?.[Flags.AS_JV_CHINA_RESTRICTED_ACCESS]?.value === FEATURE_FLAG_ON;

  if (errorFetchingOrganizations || organizations.length === 0) {
    return null;
  }

  const dropdownOptions = organizations.map((org) => {
    const isRestricted =
      isJvChinaRestrictedAccessEnabled &&
      isRestrictedOrganization(org.countries);
    const optionLabel = (
      <span className="flex items-center justify-between gap-2">
        <span className="truncate">{org.name}</span>
        {isRestricted && (
          <>
            <span className="sr-only">{restrictedOrganizationMicrocopy}</span>
            <Prohibit
              size={16}
              className="shrink-0 text-grey-400"
              aria-hidden="true"
            />
          </>
        )}
      </span>
    );

    return {
      value: org.id,
      label: optionLabel,
    };
  });

  const selectedOrgIsRestricted = activeOrganization
    ? isJvChinaRestrictedAccessEnabled &&
      isRestrictedOrganization(activeOrganization.countries)
    : false;

  const selectedOptionLabel = activeOrganization ? (
    <span className="flex items-center justify-between gap-2">
      <span className="truncate">{activeOrganization.name}</span>
      {selectedOrgIsRestricted && (
        <>
          <span className="sr-only">{restrictedOrganizationMicrocopy}</span>
          <Prohibit
            size={16}
            className="shrink-0 text-grey-400"
            aria-hidden="true"
          />
        </>
      )}
    </span>
  ) : null;

  return (
    <div className="mt-3 border-y border-grey-800 py-5 lg:px-4">
      <h2 id="switch-organization-label" className="caption mb-2 text-grey-500">
        {switchOrganizationMicrocopy}
      </h2>
      <Dropdown
        id="switch-org-dropdown"
        options={dropdownOptions}
        selectedOption={
          activeOrganization && {
            value: activeOrganization.id,
            label: selectedOptionLabel,
          }
        }
        onSelect={(option) => {
          const selectedOrganization = organizations.find(
            (org) => org.id === option.value
          );

          if (!selectedOrganization) return;

          if (isJvChinaRestrictedAccessEnabled) {
            const nextOrgIsRestricted = isRestrictedOrganization(
              selectedOrganization.countries
            );
            const isOnAccessDeniedPath = isOnJvChinaAccessDeniedPath(
              window.location.pathname
            );

            if (nextOrgIsRestricted && !isOnAccessDeniedPath) {
              handleOrgChange(selectedOrganization, false);
              router.push({
                url: `/${router.locale}${CompassUIPageRoutes.ACCESS_DENIED_JV_CHINA}`,
              });
              return;
            }

            if (!nextOrgIsRestricted && isOnAccessDeniedPath) {
              handleOrgChange(selectedOrganization, false);
              router.push({ url: `/${router.locale}` });
              return;
            }
          }

          handleOrgChange(selectedOrganization, true);
        }}
        ariaLabelledBy="switch-organization-label"
        ariaLabel={activeOrganization ? activeOrganization.name : undefined}
        maxDropdownHeight={170}
      />
    </div>
  );
};
